/**
 * Adaptive Music Hook - Phase 4: Auto-Sync BPM
 * Automatically adjusts playlist based on real-time heart rate
 * Now with unified multi-provider support
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useBPM } from './useBPM';
import { useMusicProvider } from './useMusicProvider';
import { useMusicPreload } from './useMusicPreload';
import { logger } from '@/lib/logger';
import { useToast } from './use-toast';
import type { Track } from '@/types/music-provider';
import { recordMusicAdjustment } from './useMusicMetrics';

interface AdaptiveMusicTestOverrides {
  pollIntervalMs?: number;
  rateLimitMs?: number;
  initialCheckDelayMs?: number;
}

interface UseAdaptiveMusicProps {
  isRunning: boolean;
  userAge?: number;
  autoSync?: boolean;
  bpmThreshold?: number;
  testOverrides?: AdaptiveMusicTestOverrides;
}

export function useAdaptiveMusic({ 
  isRunning, 
  userAge = 30,
  autoSync = true,
  bpmThreshold = 15,
  testOverrides
}: UseAdaptiveMusicProps) {
  const { toast } = useToast();
  const [isSynced, setIsSynced] = useState(false);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [currentPlaylist, setCurrentPlaylist] = useState<Track[]>([]);
  const lastAdjustmentRef = useRef<number>(-Infinity);
  const runningRef = useRef<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const bpm = useBPM(userAge);
  const provider = useMusicProvider();
  const { preloadTracks, isCrossfading } = useMusicPreload({ 
    crossfadeDuration: 3, 
    preloadCount: 3 
  });

  // Auto-sync playlist when BPM changes significantly
  useEffect(() => {
    if (!isRunning || !autoSync || bpm.currentBPM === 0 || !provider.isAuthenticated) {
      setIsSynced(false);
      return;
    }

    // Prevent duplicate timers (especially in React.StrictMode)
    if (intervalRef.current !== null || timeoutRef.current !== null) return;

    const checkAndAdjustPlaylist = async () => {
      if (runningRef.current) return;

      const adjustmentStartTime = Date.now();
      let metricData: {
        previous_bpm: number;
        target_bpm: number;
        bpm_difference: number;
        avg_session_bpm?: number;
        adjustment_successful: boolean;
        tracks_found: number;
        adjustment_duration_ms: number;
        provider_type: string;
        trigger_reason: string;
        user_zone?: string;
        run_id?: string;
      } | null = null;

      try {
        // Prevent too frequent adjustments
        const rateLimitMs = testOverrides?.rateLimitMs ?? 30000;
        const now = Date.now();
        const elapsed = now - lastAdjustmentRef.current;
        
        if (elapsed <= rateLimitMs) {
          logger.debug('Rate limit active, skipping adjustment', { 
            elapsed, 
            rateLimitMs,
            remaining: rateLimitMs - elapsed 
          });
          return;
        }

        // Mark as running to prevent concurrent adjustments
        if (runningRef.current) return;
        runningRef.current = true;
        
        const currentTrack = provider.currentTrack;
        if (!currentTrack) {
          runningRef.current = false;
          setIsSynced(false);
          return;
        }

        // Don't try to sync with tracks that have undefined/zero BPM
        const trackBPM = currentTrack.bpm;
        if (!trackBPM || trackBPM === 0) {
          runningRef.current = false;
          setIsSynced(false);
          logger.debug('Track has no valid BPM, marking as unsynced');
          return;
        }

        // Calculate BPM difference
        const bpmDiff = Math.abs(bpm.currentBPM - trackBPM);
        
        // If BPM difference exceeds threshold, adjust playlist
        if (bpmDiff > bpmThreshold) {
          setIsSynced(false);
          setIsAdjusting(true);
          
          logger.info('Auto-adjusting playlist', {
            currentBPM: bpm.currentBPM,
            trackBPM,
            difference: bpmDiff,
            threshold: bpmThreshold,
            provider: provider.selectedProviderType,
          });

          // Generate new playlist based on current BPM
          const targetBPM = bpm.avgBPM || bpm.currentBPM;
          const recommendations = await provider.getRecommendations(
            currentTrack.id ? [currentTrack.id] : [],
            targetBPM
          );

          const adjustmentDuration = Date.now() - adjustmentStartTime;
          const adjustmentSuccessful = recommendations.length > 0;

          // Prepare metric data
          metricData = {
            previous_bpm: trackBPM,
            target_bpm: targetBPM,
            bpm_difference: bpmDiff,
            avg_session_bpm: bpm.avgBPM || undefined,
            adjustment_successful: adjustmentSuccessful,
            tracks_found: recommendations.length,
            adjustment_duration_ms: adjustmentDuration,
            provider_type: provider.selectedProviderType || 'unknown',
            trigger_reason: 'bpm_threshold_exceeded',
            user_zone: bpm.zone ? String(bpm.zone) : undefined,
          };

          if (adjustmentSuccessful) {
            setCurrentPlaylist(recommendations);
            // Preload next tracks for smooth transitions
            preloadTracks(recommendations.slice(0, 3));
            
            // Play first track from new recommendations
            await provider.play(recommendations[0].id);
          }

          setIsSynced(adjustmentSuccessful);
          setIsAdjusting(false);
          
          // Update timestamp at the END of adjustment to include async operation duration
          lastAdjustmentRef.current = Date.now();
          
          if (adjustmentSuccessful) {
            toast({
              title: '🎵 Música Sincronizada',
              description: `Ajustado para ${targetBPM} BPM (diferença: ${bpmDiff} BPM)`,
            });
          }
        } else if (bpmDiff <= bpmThreshold / 2) {
          // Considera sincronizado se diferença for <= metade do threshold
          setIsSynced(true);
        }
      } catch (error) {
        logger.error('Failed to adjust playlist', error, {
          component: 'useAdaptiveMusic',
          currentBPM: bpm.currentBPM,
          provider: provider.selectedProviderType,
        });
        setIsAdjusting(false);

        // Record failed adjustment metric
        if (metricData) {
          metricData.adjustment_successful = false;
          metricData.adjustment_duration_ms = Date.now() - adjustmentStartTime;
        }
      } finally {
        runningRef.current = false;

        // Record telemetry metric (fire and forget)
        if (metricData) {
          recordMusicAdjustment(metricData).catch(err => 
            logger.debug('Failed to record music metric', err)
          );
        }
      }
    };

    // Check every 10 seconds (or custom interval in tests)
    const pollIntervalMs = testOverrides?.pollIntervalMs ?? 10000;
    const initialCheckDelayMs = testOverrides?.initialCheckDelayMs ?? 0;

    // Single-shot scheduling: run initial check, then start recurring interval
    timeoutRef.current = setTimeout(async () => {
      await checkAndAdjustPlaylist();
      intervalRef.current = setInterval(checkAndAdjustPlaylist, pollIntervalMs);
    }, initialCheckDelayMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isRunning, autoSync, bpmThreshold, provider.isAuthenticated, provider.isReady, provider.selectedProviderType, toast, preloadTracks]);

  // Generate initial playlist when run starts
  const startMusic = useCallback(async () => {
    if (!isRunning || bpm.currentBPM === 0 || !provider.isAuthenticated) return;

    const startTime = Date.now();

    try {
      const targetBPM = bpm.avgBPM || bpm.currentBPM || 120;
      const mood = getBPMMood(targetBPM);
      
      // Search for workout tracks based on mood and BPM
      const searchQuery = `${mood} workout running ${targetBPM} bpm`;
      const tracks = await provider.searchTracks(searchQuery, 20);

      const duration = Date.now() - startTime;
      const successful = tracks.length > 0;

      // Record metric for initial playlist
      recordMusicAdjustment({
        previous_bpm: 0,
        target_bpm: targetBPM,
        bpm_difference: 0,
        avg_session_bpm: bpm.avgBPM || undefined,
        adjustment_successful: successful,
        tracks_found: tracks.length,
        adjustment_duration_ms: duration,
        provider_type: provider.selectedProviderType || 'unknown',
        trigger_reason: 'session_start',
        user_zone: bpm.zone ? String(bpm.zone) : undefined,
      }).catch(err => logger.debug('Failed to record initial music metric', err));

      if (successful) {
        setCurrentPlaylist(tracks);
        // Preload first tracks
        preloadTracks(tracks.slice(0, 3));
        await provider.play(tracks[0].id);
        
        toast({
          title: '🎵 Playlist Iniciada',
          description: `${tracks.length} músicas encontradas para ${targetBPM} BPM`,
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Nenhuma música encontrada',
          description: 'Tente ajustar seu BPM ou conectar-se a outro serviço',
        });
      }
    } catch (error) {
      logger.error('Failed to start music', error);
      
      // Record failed metric
      recordMusicAdjustment({
        previous_bpm: 0,
        target_bpm: bpm.avgBPM || bpm.currentBPM || 120,
        bpm_difference: 0,
        adjustment_successful: false,
        tracks_found: 0,
        adjustment_duration_ms: Date.now() - startTime,
        provider_type: provider.selectedProviderType || 'unknown',
        trigger_reason: 'session_start',
        user_zone: bpm.zone ? String(bpm.zone) : undefined,
      }).catch(err => logger.debug('Failed to record failed music metric', err));

      toast({
        variant: 'destructive',
        title: 'Erro ao iniciar música',
        description: 'Verifique sua conexão com o serviço de música',
      });
    }
  }, [isRunning, bpm.currentBPM, bpm.avgBPM, bpm.zone, provider, toast, preloadTracks]);

  return {
    // BPM data
    currentBPM: bpm.currentBPM,
    avgBPM: bpm.avgBPM,
    maxBPM: bpm.maxBPM,
    minBPM: bpm.minBPM,
    zone: bpm.zone,
    isMonitoring: bpm.isMonitoring,
    
    // Music data
    currentTrack: provider.currentTrack,
    playlist: currentPlaylist,
    isPlaying: provider.playerState?.isPlaying || false,
    isSynced,
    isAdjusting,
    isCrossfading,
    bpmThreshold,
    
    // Provider data
    providerType: provider.selectedProviderType,
    providerName: provider.currentProvider?.name,
    isAuthenticated: provider.isAuthenticated,
    isReady: provider.isReady,
    playerState: provider.playerState,
    
    // Controls
    startMusic,
    pause: provider.pause,
    resume: provider.resume,
    next: provider.next,
    previous: provider.previous,
    seek: provider.seek,
    setVolume: provider.setVolume,
    
    // Provider management
    switchProvider: provider.switchProvider,
    connect: provider.connect,
    disconnect: provider.disconnect,
  };
}

function getBPMMood(bpm: number): 'calm' | 'energized' | 'focused' | 'motivated' {
  if (bpm < 100) return 'calm';
  if (bpm < 140) return 'focused';
  if (bpm < 160) return 'energized';
  return 'motivated';
}

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const hour = new Date().getHours();
  if (hour < 6) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function getEnergyLevel(bpm: number): number {
  return Math.min(100, Math.max(0, ((bpm - 60) / 140) * 100));
}
